import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiChevronRight,
  FiPhone,
  FiMail,
  FiMapPin,
  FiChevronDown,
  FiArrowUpRight,
} from "react-icons/fi";
import staticLogo from "../assets/uploads/logos/1780688835226-584334.jpg";
import "./Navbar.css";

function toPath(link) {
  if (!link) return "/";
  const clean = link.replace(/\.php$/, "");
  if (clean.startsWith("/")) return clean;
  return `/${clean}`;
}

function toChildPath(childLink, parentPath) {
  const childPath = toPath(childLink);
  if (childPath.startsWith(parentPath + "/") || childPath.startsWith(parentPath + "?")) {
    return childPath;
  }
  const slug = childPath.startsWith("/") ? childPath.slice(1) : childPath;
  return `${parentPath}/${slug}`;
}

function dropKey(item) {
  return String(item.id);
}

export default function Navbar() {
  const [navItems, setNavItems] = useState([]);
  const [contact,  setContact]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  const [scrolled,   setScrolled]   = useState(false);
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [openDrop,   setOpenDrop]   = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);

  const location   = useLocation();
  const dropTimers = useRef({});
  const dropRefs   = useRef({});

  useEffect(() => {
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/api/navbar`).then(r => r.json()),
      fetch(`${process.env.REACT_APP_API_URL}/api/company-contact`).then(r => r.json()),
    ])
      .then(([nav, contactData]) => {
        setNavItems(Array.isArray(nav) ? nav : []);
        setContact(contactData?.id ? contactData : null);
      })
      .catch(err => console.error("Navbar data fetch failed:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenDrop(null);
    setMobileOpen(null);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    const handler = (e) => {
      const clickedAny = Object.values(dropRefs.current).some(
        ref => ref && ref.contains(e.target)
      );
      if (!clickedAny) setOpenDrop(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleEnter = (key) => {
    clearTimeout(dropTimers.current[key]);
    setOpenDrop(key);
  };

  const handleLeave = (key) => {
    dropTimers.current[key] = setTimeout(
      () => setOpenDrop(d => d === key ? null : d),
      180
    );
  };

  const isParentActive = (item) => {
    const parentPath = toPath(item.link);
    if (location.pathname === parentPath) return true;
    if (item.children && item.children.length > 0) {
      return item.children.some(
        c => location.pathname === toChildPath(c.link, parentPath)
      );
    }
    return false;
  };

  const getDropMeta = (label) => {
    const map = {
      services:  { eyebrow: "WHAT WE DO",  heading: "Our\nServices",     isServices: true  },
      about:     { eyebrow: "ABOUT US",     heading: "Who\nWe Are",       isServices: false },
      portfolio: { eyebrow: "PROJECTS",     heading: "Explore\nOur Work", isServices: false },
      products:  { eyebrow: "OUR RANGE",    heading: "Our\nProducts",     isServices: true  },
    };
    return map[label?.toLowerCase()] || { eyebrow: label?.toUpperCase(), heading: label, isServices: false };
  };

  if (loading) {
    return (
      <div className="header-wrapper">
        <div className="topbar">
          <div className="topbar__inner">
            <div className="topbar__slogan">⚡ Loading…</div>
          </div>
        </div>
        <nav className="navbar">
          <div className="navbar__inner">
            <Link to="/" className="navbar__logo">
              <img src={staticLogo} alt="Logo" className="navbar__logo-image" />
            </Link>
          </div>
        </nav>
      </div>
    );
  }

  const phone       = contact?.phone_number         || "";
  const email       = contact?.email_address        || "";
  const address     = contact?.physical_address     || "";
  const addressLink = contact?.physical_address_link
    || `https://maps.google.com/?q=${encodeURIComponent(address)}`;

  const filteredNavItems = navItems.filter(
    item => toPath(item.link) !== "/shop"
  );

  return (
    <>
      <div className="header-wrapper">

        {/* ── Top Bar ── */}
        <div className="topbar">
          <div className="topbar__inner">
            <div className="topbar__slogan">
              ⚡ We go through all diagnosis stages
            </div>

            <div className="topbar__contact">
              {address && (
                <a
                  href={addressLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="topbar__item"
                >
                  <span className="topbar__icon-circle"><FiMapPin size={12} /></span>
                  {address}
                </a>
              )}

              {address && email && (
                <span className="topbar__divider">|</span>
              )}

              {email && (
                <a href={`mailto:${email}`} className="topbar__item">
                  <span className="topbar__icon-circle"><FiMail size={12} /></span>
                  {email}
                </a>
              )}

              {email && phone && (
                <span className="topbar__divider">|</span>
              )}

              {phone && (
                <a
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className="topbar__item"
                >
                  <span className="topbar__icon-circle"><FiPhone size={12} /></span>
                  {phone}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── Main Navbar ── */}
        <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
          <div className="navbar__inner">

            {/* Logo */}
            <Link to="/" className="navbar__logo">
              <img src={staticLogo} alt="Logo" className="navbar__logo-image" />
            </Link>

            {/* Desktop nav links */}
            <ul className="navbar__links">
              {/* BUY SPARE PARTS as first nav tab — desktop only */}
              <li>
                <Link
                  to="/shop"
                  className={`navbar__link navbar__link--shop${location.pathname === "/shop" ? " navbar__link--active" : ""}`}
                >
                  Buy Spare Parts
                </Link>
              </li>

              {filteredNavItems.map((item) => {
                const key         = dropKey(item);
                const hasDropdown = item.children && item.children.length > 0;
                const active      = isParentActive(item);
                const path        = toPath(item.link);

                if (!hasDropdown) {
                  return (
                    <li key={item.id}>
                      <Link
                        to={path}
                        className={`navbar__link${location.pathname === path ? " navbar__link--active" : ""}`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                }

                const meta       = getDropMeta(item.label);
                const isServices = meta.isServices;
                const isOpen     = openDrop === key;

                return (
                  <li
                    key={item.id}
                    className="navbar__item--has-drop"
                    ref={el => dropRefs.current[key] = el}
                    onMouseEnter={() => handleEnter(key)}
                    onMouseLeave={() => handleLeave(key)}
                  >
                    <button
                      className={`navbar__link navbar__link--drop-trigger${active ? " navbar__link--active" : ""}`}
                      onClick={() => setOpenDrop(d => d === key ? null : key)}
                      aria-expanded={isOpen}
                      type="button"
                    >
                      {item.label}
                      <FiChevronDown
                        size={13}
                        className={`navbar__drop-chevron${isOpen ? " navbar__drop-chevron--open" : ""}`}
                      />
                    </button>

                    <div className={`navbar__dropdown${isServices ? " navbar__dropdown--services" : ""}${isOpen ? " navbar__dropdown--open" : ""}`}>
                      <div className={`navbar__drop-inner${isServices ? " navbar__drop-inner--services" : ""}`}>

                        <div className="navbar__drop-label-col">
                          <span className="navbar__drop-eyebrow">{meta.eyebrow}</span>
                          <h3 className="navbar__drop-heading">
                            {meta.heading.split("\n").map((line, i, arr) => (
                              <React.Fragment key={i}>
                                {line}
                                {i < arr.length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </h3>
                          <div className="navbar__drop-bar" />
                        </div>

                        {isServices ? (
                          <div className="navbar__drop-links-wrapper">
                            <Link
                              to={path}
                              className={`navbar__drop-all-services${location.pathname === path ? " navbar__drop-all-services--active" : ""}`}
                            >
                              <div className="navbar__drop-all-services-text">
                                <span className="navbar__drop-all-services-label">All {item.label}</span>
                                <span className="navbar__drop-all-services-desc">Browse everything we offer</span>
                              </div>
                              <FiArrowUpRight size={16} className="navbar__drop-all-services-icon" />
                            </Link>
                            <div className="navbar__drop-links navbar__drop-links--grid">
                              {item.children.map((child, i) => {
                                const childPath = toChildPath(child.link, path);
                                return (
                                  <Link
                                    key={child.id}
                                    to={childPath}
                                    className={`navbar__drop-link navbar__drop-link--compact${location.pathname === childPath ? " navbar__drop-link--active" : ""}`}
                                    style={{ animationDelay: `${i * 0.04}s` }}
                                  >
                                    <div className="navbar__drop-link-inner">
                                      <div className="navbar__drop-link-text">
                                        <span className="navbar__drop-link-label">{child.label}</span>
                                      </div>
                                      <FiArrowUpRight size={16} className="navbar__drop-link-icon" />
                                    </div>
                                    <div className="navbar__drop-link-line" />
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="navbar__drop-links">
                            {item.children.map((child, i) => {
                              const childPath = toChildPath(child.link, path);
                              return (
                                <Link
                                  key={child.id}
                                  to={childPath}
                                  className={`navbar__drop-link${location.pathname === childPath ? " navbar__drop-link--active" : ""}`}
                                  style={{ animationDelay: `${i * 0.05}s` }}
                                >
                                  <div className="navbar__drop-link-inner">
                                    <div className="navbar__drop-link-text">
                                      <span className="navbar__drop-link-label">{child.label}</span>
                                    </div>
                                    <FiArrowUpRight size={18} className="navbar__drop-link-icon" />
                                  </div>
                                  <div className="navbar__drop-link-line" />
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* ── BUY HERE button — visible on mobile next to hamburger ── */}
            <Link
              to="/shop"
              className={`navbar__shop-inline${location.pathname === "/shop" ? " navbar__shop-inline--active" : ""}`}
              aria-label="Buy spare parts"
            >
              BUY HERE
            </Link>

            <button
              className="navbar__hamburger"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </nav>
      </div>

      {/* ── Mobile Menu ── */}
      <div className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`}>
        <ul className="mobile-menu__links">
          {/* BUY SPARE PARTS kept in mobile menu too for completeness */}
          <li style={{ animationDelay: "0s" }}>
            <Link
              to="/shop"
              className={`mobile-menu__link mobile-menu__link--shop${location.pathname === "/shop" ? " mobile-menu__link--active" : ""}`}
            >
              <FiChevronRight size={18} />
              Buy Spare Parts
            </Link>
          </li>

          {filteredNavItems.map((item, i) => {
            const key         = dropKey(item);
            const hasDropdown = item.children && item.children.length > 0;
            const active      = isParentActive(item);
            const path        = toPath(item.link);
            const isOpen      = mobileOpen === key;

            if (!hasDropdown) {
              return (
                <li key={item.id} style={{ animationDelay: `${(i + 1) * 0.05}s` }}>
                  <Link
                    to={path}
                    className={`mobile-menu__link${location.pathname === path ? " mobile-menu__link--active" : ""}`}
                  >
                    <FiChevronRight size={18} />
                    {item.label}
                  </Link>
                </li>
              );
            }

            return (
              <li key={item.id} style={{ animationDelay: `${(i + 1) * 0.05}s` }}>
                <button
                  className={`mobile-menu__link mobile-menu__link--has-drop${active ? " mobile-menu__link--active" : ""}`}
                  onClick={() => setMobileOpen(v => v === key ? null : key)}
                  type="button"
                >
                  <FiChevronRight size={18} />
                  {item.label}
                  <FiChevronDown
                    size={14}
                    className={`mobile-drop-chevron${isOpen ? " mobile-drop-chevron--open" : ""}`}
                    style={{ marginLeft: "auto" }}
                  />
                </button>
                {isOpen && (
                  <ul className="mobile-menu__submenu">
                    <li>
                      <Link
                        to={path}
                        className={`mobile-menu__sublink mobile-menu__sublink--all${location.pathname === path ? " mobile-menu__sublink--active" : ""}`}
                      >
                        <FiArrowUpRight size={14} />
                        All {item.label}
                      </Link>
                    </li>
                    {item.children.map((child) => {
                      const childPath = toChildPath(child.link, path);
                      return (
                        <li key={child.id}>
                          <Link
                            to={childPath}
                            className={`mobile-menu__sublink${location.pathname === childPath ? " mobile-menu__sublink--active" : ""}`}
                          >
                            <FiArrowUpRight size={14} />
                            {child.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        <Link
          to="/contact"
          className="btn-primary mobile-menu__btn"
          onClick={() => setMenuOpen(false)}
        >
          Get a Quote
        </Link>

        <div className="mobile-menu__footer">
          <p>© 2025 Noel Auto (Pty) Ltd</p>
        </div>
      </div>

      {menuOpen && (
        <div className="mobile-overlay" onClick={() => setMenuOpen(false)} />
      )}
    </>
  );
}